import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import StarIcon from "@mui/icons-material/Star";
import { Button, Card, CardActions, CardHeader, IconButton, Stack } from "@mui/material";

import { machineGroupSchema, type MachineGroupStatus } from "@/schemas";
import { getMachineGroupStatusMessage } from "@/views/production/components/machine-group-status/machine-group-status-utils";

const getStatusColor = (
	status: MachineGroupStatus,
): "success.main" | "warning.main" | "error.main" | "text.disabled" => {
	switch (status) {
		case "Online":
			return "success.main";
		case "Paused":
		case "ChangingCorrugate":
			return "warning.main";
		case "Error":
			return "error.main";
		default:
			return "text.disabled";
	}
};

export interface MachineGroupCardProps {
	readonly machineGroup: unknown;
	onClick(): void;
	onFavoriteClick(): void;
	readonly isFavorite: boolean;
}

export const MachineGroupCard: React.FC<MachineGroupCardProps> = ({
	machineGroup,
	onClick,
	onFavoriteClick,
	isFavorite,
}) => {
	const intl = useIntl();
	const validationResult = machineGroupSchema.safeParseWithTelemetry(machineGroup);

	if (validationResult.success === false) {
		return null;
	}

	const handleFavoriteClicked = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation();
		onFavoriteClick();
	};

	const { alias, status } = validationResult.data;

	return (
		<Card
			component={Stack}
			onClick={onClick}
			sx={{
				cursor: "pointer",
				justifyContent: "space-between",
			}}
			data-testid="machine-group-card-container"
		>
			<CardHeader
				title={alias}
				subheader={getMachineGroupStatusMessage(status, intl)}
				action={
					<IconButton
						size="small"
						type="button"
						onClick={(e) => handleFavoriteClicked(e)}
						data-testid="favorite-button"
					>
						{isFavorite === true ? <StarIcon sx={{ color: "warning.main" }} /> : <StarBorderIcon />}
					</IconButton>
				}
				slotProps={{
					subheader: {
						sx: {
							color: getStatusColor(status),
						},
					},
				}}
			/>

			<CardActions>
				<Button
					variant="contained"
					type="button"
					onClick={onClick}
					fullWidth
					data-cy={`use-mg-${alias}`}
					data-testid="use-machine-group-button"
				>
					<FormattedMessage id="Use Machine Group" defaultMessage="Use Machine Group" />
				</Button>
			</CardActions>
		</Card>
	);
};
